import { useCallback, useMemo, useState } from 'react'
import zionDebug from '@fs/zion-debug'
import { getNestedOrFloatingElementById, getPersonName, getPersonRelationships, useSlsStateContext } from '@fs/zion-sls'
import { getAtRiskRelationshipGroups, hasAtRiskRelationships } from './utils/atRiskRelationships'
import { ADD_EXISTING_PERSON_ENTRY_POINTS, ADD_EXISTING_PERSON_STEPS } from './utils/addExistingPersonConstants'

// To turn this on, in the browser console: localStorage.debug = 'fs:rip:addExistingPerson'
const debug = zionDebug('rip:addExistingPerson')

/**
 * Resolves person ids to the shape the at-risk calculation needs.
 *
 * @param {Array<string>} personIds - Selected person ids
 * @param {Array<Object>} images - SLS images from state
 * @returns {Array<{id: string, name: string}>}
 */
const toMovingPersons = (personIds, images) =>
  personIds.map((id) => {
    const person = getNestedOrFloatingElementById(images, id)
    return { id, name: person ? getPersonName({ person, justOneName: true }) : '' }
  })

/**
 * Groups the relationships a move would break, for the given selection.
 *
 * Kept as a plain function so it can be called with a selection that has not been committed to
 * state yet, which is what the selection step needs in order to decide the next step.
 *
 * @param {Array<string>} personIds - Selected person ids
 * @param {Array<Object>} images - SLS images from state
 * @param {Array<string>} pendingMemberIds - Ids already staged to join the same group
 * @returns {Array<import('./utils/atRiskRelationships').AtRiskRelationshipGroup>}
 */
const buildAtRiskGroups = (personIds, images, pendingMemberIds) => {
  const relationshipsByPersonId = personIds.reduce((byId, id) => {
    const person = getNestedOrFloatingElementById(images, id)
    byId[id] = person ? getPersonRelationships(person, images, false) || [] : []
    return byId
  }, {})

  return getAtRiskRelationshipGroups({
    movingPersons: toMovingPersons(personIds, images),
    relationshipsByPersonId,
    pendingMemberIds,
  })
}

/**
 * @typedef {Object} AddExistingPersonResult
 * @property {Array<string>} personIds - Everyone the patron chose to move
 * @property {Array<string>} primaryPersonIds - Who should be primary in the resulting group. Empty when
 *   joining a group that already has one, since an add confers primacy on nobody.
 * @property {Array<string>} relationshipIdsToRemove - Relationships the patron agreed to break
 */

/**
 * @typedef {Object} UseAddExistingPersonFlowResult
 * @property {string} step - Current step, one of {@link ADD_EXISTING_PERSON_STEPS}
 * @property {Array<{id: string, name: string}>} movingPersons - The selected people, with display names
 * @property {Array<import('./utils/atRiskRelationships').AtRiskRelationshipGroup>} atRiskGroups
 *   Relationships the move would break, grouped by moving person
 * @property {boolean} previousRelationshipsApplied - Whether the confirmation step was shown this run
 * @property {(personIds: Array<string>) => void} selectPersons - Advance from the selection sheet
 * @property {() => void} confirmRelationshipRemoval - Advance past the Previous Relationships step
 * @property {(primaryPersonIds: Array<string>) => void} confirmPrimaryPersons - Finish from the Primary Person step
 * @property {() => void} goBackToPreviousRelationships - Return to the Previous Relationships step
 * @property {() => void} reset - Return the flow to its initial state
 */

/**
 * Drives the Add Existing Person flow across its three steps.
 *
 * Both follow-up steps are conditional. The Previous Relationships confirmation fires once per
 * action — not once per person — and only when somebody would lose a relationship to a person left
 * behind. The Primary Person step only applies when creating a new group with more than one person
 * moving; a lone mover is auto-set primary, so there is nothing to ask.
 *
 * @param {Object} params
 * @param {string} params.entryPoint - One of {@link ADD_EXISTING_PERSON_ENTRY_POINTS}
 * @param {Array<string>} [params.pendingMemberIds] - Ids already staged to join the group
 * @param {(result: AddExistingPersonResult) => void} params.onComplete - Called once every applicable step is answered
 * @returns {UseAddExistingPersonFlowResult}
 */
export const useAddExistingPersonFlow = ({ entryPoint, pendingMemberIds = [], onComplete = () => {} }) => {
  const { images } = useSlsStateContext()
  const [step, setStep] = useState(ADD_EXISTING_PERSON_STEPS.SELECT)
  const [selectedPersonIds, setSelectedPersonIds] = useState([])
  const [previousRelationshipsApplied, setPreviousRelationshipsApplied] = useState(false)

  const movingPersons = useMemo(() => toMovingPersons(selectedPersonIds, images), [selectedPersonIds, images])
  const atRiskGroups = useMemo(
    () => buildAtRiskGroups(selectedPersonIds, images, pendingMemberIds),
    [selectedPersonIds, images, pendingMemberIds]
  )

  const reset = useCallback(() => {
    setStep(ADD_EXISTING_PERSON_STEPS.SELECT)
    setSelectedPersonIds([])
    setPreviousRelationshipsApplied(false)
  }, [])

  /**
   * Either asks who is primary, or finishes the flow.
   *
   * @param {Array<string>} personIds - The committed selection
   * @returns {void}
   */
  const advancePastRelationships = useCallback(
    (personIds) => {
      if (entryPoint === ADD_EXISTING_PERSON_ENTRY_POINTS.NEW_GROUP && personIds.length > 1) {
        setStep(ADD_EXISTING_PERSON_STEPS.PRIMARY_PERSON)
        return
      }
      const relationshipIdsToRemove = buildAtRiskGroups(personIds, images, pendingMemberIds).flatMap((group) =>
        group.relationships.map((relationship) => relationship.id)
      )
      // Only a new group confers primacy, and by here it holds a lone mover, who is auto-set primary.
      // Joining a group that already has one makes nobody primary.
      const primaryPersonIds = entryPoint === ADD_EXISTING_PERSON_ENTRY_POINTS.NEW_GROUP ? personIds : []

      debug('flow complete', { personIds, primaryPersonIds, relationshipIdsToRemove })
      // Reset first: it closes the dialog, and the caller's handler closes the sheet. Closing
      // inner-to-outer leaves focus on the outermost surface rather than inside a sheet that has gone.
      reset()
      onComplete({ personIds, primaryPersonIds, relationshipIdsToRemove })
    },
    [entryPoint, images, pendingMemberIds, onComplete, reset]
  )

  const selectPersons = useCallback(
    (personIds) => {
      setSelectedPersonIds(personIds)
      const atRisk = buildAtRiskGroups(personIds, images, pendingMemberIds)

      // Neither the sheet nor the dialogs show why a confirmation was skipped.
      debug('selection confirmed', {
        personIds,
        entryPoint,
        atRiskGroups: atRisk,
        needsPreviousRelationshipsStep: hasAtRiskRelationships(atRisk),
        needsPrimaryPersonStep: entryPoint === ADD_EXISTING_PERSON_ENTRY_POINTS.NEW_GROUP && personIds.length > 1,
      })

      if (hasAtRiskRelationships(atRisk)) {
        setPreviousRelationshipsApplied(true)
        setStep(ADD_EXISTING_PERSON_STEPS.PREVIOUS_RELATIONSHIPS)
        return
      }

      setPreviousRelationshipsApplied(false)
      advancePastRelationships(personIds)
    },
    [images, entryPoint, pendingMemberIds, advancePastRelationships]
  )

  const confirmRelationshipRemoval = useCallback(
    () => advancePastRelationships(selectedPersonIds),
    [advancePastRelationships, selectedPersonIds]
  )

  const confirmPrimaryPersons = useCallback(
    (primaryPersonIds) => {
      const relationshipIdsToRemove = atRiskGroups.flatMap((group) =>
        group.relationships.map((relationship) => relationship.id)
      )
      const personIds = selectedPersonIds
      debug('flow complete', { personIds, primaryPersonIds, relationshipIdsToRemove })
      // Read before resetting, then close inner-to-outer. See advancePastRelationships.
      reset()
      onComplete({ personIds, primaryPersonIds, relationshipIdsToRemove })
    },
    [atRiskGroups, onComplete, selectedPersonIds, reset]
  )

  const goBackToPreviousRelationships = useCallback(() => setStep(ADD_EXISTING_PERSON_STEPS.PREVIOUS_RELATIONSHIPS), [])

  return {
    step,
    movingPersons,
    atRiskGroups,
    previousRelationshipsApplied,
    selectPersons,
    confirmRelationshipRemoval,
    confirmPrimaryPersons,
    goBackToPreviousRelationships,
    reset,
  }
}

export default useAddExistingPersonFlow
